import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import type { AuthResponse, MeResponse, SessionUser, VerificationMailResponse } from '@haplika/contracts';
import { ApiError, api, post } from '../lib/api';

const messageOf = (e: unknown) => e instanceof Error ? e.message : 'No pudimos completar el pedido. Probá de nuevo.';
export const accountDestination = (user: SessionUser) => user.role === 'company' ? '/empresas/nueva' : '/app/inicio';
/** Después de un link del mail: las cuentas del equipo sin perfil de candidato ni empresa vuelven al backoffice. */
async function linkDestination(user: SessionUser): Promise<string> {
  const me = await api<MeResponse>('/auth/me').catch(() => null);
  return me?.isAdmin && !me.candidate && !me.org ? '/admin' : accountDestination(user);
}
export const verificationCooldownKey = (email: string) => `haplika.verificationUntil:${email.toLowerCase()}`;

function Page({ title, children }: { title: string; children: ReactNode }) {
  return <main style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24, background: 'var(--miga)' }}>
    <section className="card stack" style={{ width: 'min(440px, 100%)', padding: 28 }}>
      <img src="/assets/haplika-logotipo.svg" alt="haplika" style={{ height: 28, alignSelf: 'start' }} />
      <h1 className="t-h2">{title}</h1>{children}
    </section>
  </main>;
}

export function VerificationGate({ user, onDone, mailSent, exitTo = '/app/ingresar' }: { user: SessionUser; onDone: () => void; mailSent?: boolean; exitTo?: string }) {
  const [email, setEmail] = useState(user.email ?? '');
  const [sentEmail, setSentEmail] = useState(user.email ?? '');
  const [changing, setChanging] = useState(false);
  const [until, setUntil] = useState(() => Number(sessionStorage.getItem(verificationCooldownKey(user.email ?? ''))) || 0);
  const [now, setNow] = useState(Date.now);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(mailSent === false ? 'No pudimos mandar el mail. Tocá Reenviar para probar otra vez.' : 'Abrí el link del mail para verificar tu cuenta.');
  const [error, setError] = useState('');
  useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const seconds = Math.max(0, Math.ceil((until - now) / 1000));
  const send = async (e?: FormEvent) => {
    e?.preventDefault(); setBusy(true); setError('');
    try {
      const r = await post<VerificationMailResponse>(changing ? '/auth/verification/email' : '/auth/verification/resend', changing ? { email: email.trim() } : undefined);
      const next = Date.now() + r.retryAfterSeconds * 1000;
      sessionStorage.setItem(verificationCooldownKey(r.email), String(next));
      sessionStorage.setItem('haplika.verificationMailSent', 'true');
      setEmail(r.email); setSentEmail(r.email); setUntil(next); setChanging(false); setNote('Te mandamos un nuevo link. Revisá también el correo no deseado.');
    } catch (e) { setError(messageOf(e)); if (e instanceof ApiError && e.status === 429) setUntil(Date.now() + 60000); }
    finally { setBusy(false); }
  };
  const check = async () => {
    setBusy(true); setError('');
    try { const m = await api<MeResponse>('/auth/me'); if (m.user.emailVerified !== false) onDone(); else setNote('Todavía falta verificar. Abrí el link que te mandamos.'); }
    catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  return <Page title="Revisá tu mail">
    <p>Te escribimos a <b>{sentEmail}</b>.</p>
    <p className="t-muted" role="status">{note}</p>
    {changing ? <form className="stack" onSubmit={(e) => void send(e)}>
      <label className="field">Tu email<input className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
      <button className="btn btn-primary" disabled={busy || seconds > 0}>Guardar y mandar link{seconds > 0 ? ` (${seconds} s)` : ''}</button>
      <button className="btn btn-ghost" type="button" onClick={() => { setEmail(sentEmail); setChanging(false); }}>Volver</button>
    </form> : <>
      <button className="btn btn-primary" disabled={busy || seconds > 0} onClick={() => void send()}>{seconds ? `Reenviar en ${seconds} s` : 'Reenviar'}</button>
      <button className="btn btn-ghost" onClick={() => setChanging(true)}>Cambiar el email</button>
    </>}
    {error && <p role="alert" style={{ color: 'var(--tomate-texto)' }}>{error}</p>}
    <button className="btn btn-secondary" disabled={busy} onClick={() => void check()}>Ya verifiqué mi cuenta</button>
    <button className="btn btn-ghost" onClick={() => void post('/auth/logout').then(() => window.location.assign(exitTo)).catch((e: unknown) => setError(messageOf(e)))}>Cerrar sesión</button>
  </Page>;
}

export function PasswordGate({ user, onDone }: { user: SessionUser; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const act = async (skip: boolean) => {
    setBusy(true); setError('');
    try { await post<AuthResponse>(skip ? '/auth/password/skip' : '/auth/change-password', skip ? undefined : { password, ...(!user.passwordChangeRequired ? { currentPassword } : {}) }); onDone(); }
    catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  return <Page title="Elegí tu contraseña"><p className="t-muted">Elegí una contraseña propia para entrar a haplika.</p>
    <form className="stack" onSubmit={(e) => { e.preventDefault(); void act(false); }}>
      {!user.passwordChangeRequired && <label className="field">Contraseña actual<input className="input" type="password" autoComplete="current-password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} /></label>}
      <label className="field">Nueva contraseña<input className="input" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} /><span className="hint">Al menos 8 caracteres</span></label>
      {error && <p role="alert" style={{ color: 'var(--tomate-texto)' }}>{error}</p>}
      <button className="btn btn-primary" disabled={busy}>Guardar y entrar</button>
    </form>
    {user.canSkipPasswordChange && <button className="btn btn-ghost" disabled={busy} onClick={() => void act(true)}>Más tarde</button>}
  </Page>;
}

export default function AuthPage() {
  const location = useLocation();
  const kind = location.pathname.split('/').pop();
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('token') ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(kind === 'verify-email' && !!token);
  const [expired, setExpired] = useState(!token && kind !== 'forgot-password' && kind !== 'change-password');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [attempt, setAttempt] = useState(0);
  const verification = useRef<Promise<AuthResponse> | null>(null);
  useEffect(() => {
    // El fragmento nunca sale en el Referer ni queda en el historial después de capturarlo.
    if (location.hash) window.history.replaceState(window.history.state, '', location.pathname + location.search);
    if (kind === 'verify-email' && token) {
      setBusy(true); setError('');
      verification.current ??= post<AuthResponse>('/auth/verify-email', { token });
      void verification.current.then(async (r) => window.location.replace(await linkDestination(r.user))).catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 410) setExpired(true); else setError(messageOf(e));
      }).finally(() => setBusy(false));
    }
    if (kind === 'change-password') void api<MeResponse>('/auth/me').then(setMe).catch((e: unknown) => setError(messageOf(e)));
  }, [kind, token, location.hash, location.pathname, location.search, attempt]);
  if (kind === 'change-password' && me) return <PasswordGate user={me.user} onDone={() => window.location.replace(accountDestination(me.user))} />;
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      if (kind === 'reset-password' && !expired) {
        const r = await post<AuthResponse>('/auth/reset-password', { token, password }); window.location.replace(await linkDestination(r.user));
      } else {
        // El token vencido puede ya no estar (purga diaria): el email es lo que permite mandar otro.
        await post(kind === 'verify-email' ? '/auth/verification/request' : '/auth/forgot-password', kind === 'verify-email' && token ? { email: email.trim(), token } : { email: email.trim() });
        setNote(kind === 'verify-email' ? 'Si tu cuenta todavía necesita verificación, te mandamos otro link.' : 'Si hay una cuenta con ese email, te mandamos un link');
      }
    } catch (e) { if (e instanceof ApiError && e.status === 410) setExpired(true); else setError(messageOf(e)); }
    finally { setBusy(false); }
  };
  const title = expired ? 'Este link ya no sirve' : kind === 'verify-email' ? 'Verificando tu cuenta…' : kind === 'reset-password' ? 'Elegí tu nueva contraseña' : kind === 'change-password' ? 'Elegí tu contraseña' : 'Recuperá tu contraseña';
  return <Page title={title}>
    {busy && <p role="status">Un momento…</p>}
    {expired && !note && <p className="t-muted">Escribí tu email y te mandamos uno nuevo.</p>}
    {note ? <p role="status">{note}</p> : (kind !== 'verify-email' || expired) && kind !== 'change-password' ? <form className="stack" onSubmit={(e) => void submit(e)}>
      {kind === 'reset-password' && !expired ? <label className="field">Nueva contraseña<input className="input" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} /><span className="hint">Al menos 8 caracteres</span></label> : <label className="field">Email<input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>}
      <button className="btn btn-primary" disabled={busy}>{expired ? 'Mandame otro' : kind === 'reset-password' ? 'Guardar y entrar' : 'Mandame el link'}</button>
    </form> : null}
    {error && <p role="alert" style={{ color: 'var(--tomate-texto)' }}>{error}</p>}
    {error && kind === 'verify-email' && !expired && <button className="btn btn-secondary" disabled={busy} onClick={() => { verification.current = null; setAttempt((n) => n + 1); }}>Volver a intentar</button>}
    <Link to={new URLSearchParams(location.search).get('role') === 'company' ? '/empresas/acceso' : '/app/ingresar'}>Volver al ingreso</Link>
  </Page>;
}

/** Se ejecuta antes de montar Empresas: /me puede no traer org en una sesión restringida. */
export function CompanyAccountBoundary({ children }: { children: ReactNode }) {
  const [check, setCheck] = useState<{ me: MeResponse | null } | null>(null);
  useEffect(() => { let alive = true; void api<MeResponse>('/auth/me').then((me) => { if (alive) setCheck({ me }); }).catch(() => { if (alive) setCheck({ me: null }); }); return () => { alive = false; }; }, []);
  if (!check) return <Page title="Un momento…"><p role="status">Cargando tu cuenta</p></Page>;
  if (check.me?.user.emailVerified === false) return <VerificationGate user={check.me.user} onDone={() => window.location.reload()} exitTo="/empresas/acceso" />;
  if (check.me?.user.passwordChangeRequired) return <PasswordGate user={check.me.user} onDone={() => window.location.reload()} />;
  return children;
}
